import { Link } from "react-router-dom";
import { resolveImageUrl } from "@/lib/api";
import { Heart, MessageCircle } from "lucide-react";

/* Mosaic preview: render up to 6 canvas items proportionally on a white square. */
function MosaicPreview({ items = [], cover }) {
  const hasItems = items?.length > 0;
  if (!hasItems && cover) {
    return (
      <div className="absolute inset-0 bg-white flex items-center justify-center">
        <img src={resolveImageUrl(cover)} alt="" className="max-w-[80%] max-h-[80%] object-contain" />
      </div>
    );
  }
  if (!hasItems) return <div className="w-full h-full bg-[#F2EDE3]" />;
  return (
    <div className="absolute inset-0 bg-white">
      {items.slice(0, 12).map((it, i) => {
        const sx = it.flipped_x ? -1 : 1;
        const sy = it.flipped_y ? -1 : 1;
        return (
          <img
            key={i}
            src={resolveImageUrl(it.image_url)}
            alt=""
            className="absolute object-contain"
            style={{
              left: `${(it.x / 800) * 100}%`,
              top: `${(it.y / 800) * 100}%`,
              width: `${(it.width / 800) * 100}%`,
              height: `${(it.height / 800) * 100}%`,
              transform: `rotate(${it.rotation || 0}deg) scale(${sx}, ${sy})`,
              zIndex: it.z_index || 1,
            }}
          />
        );
      })}
    </div>
  );
}

export default function SetCard({ set, hidePalette = false }) {
  return (
    <Link
      data-testid={`set-card-${set.id}`}
      to={`/sets/${set.id}`}
      className="group block bg-white border border-[#EFEFEF] hover:border-[#333333] transition-colors"
    >
      <div className="relative aspect-square w-full overflow-hidden">
        <MosaicPreview items={set.items} cover={set.cover_image} />
        <div className="absolute top-2 left-2 flex items-center gap-1.5">
          <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#333333]/60 bg-white/70 backdrop-blur px-1.5 py-0.5">
            {set.category}
          </div>
          {set.featured && (
            <div data-testid="featured-badge" className="font-mono text-[9px] uppercase tracking-[0.2em] text-white bg-[#9CA47E] px-1.5 py-0.5">
              ◆ Featured
            </div>
          )}
        </div>
        {!hidePalette && set.palette?.length > 0 && (
          <div className="absolute bottom-2 left-2 flex gap-0.5">
            {set.palette.slice(0, 5).map((c, i) => (
              <span
                key={i}
                title={c}
                className="w-3 h-3 border border-white/80 shadow-[0_0_0_1px_rgba(0,0,0,0.06)]"
                style={{ background: c }}
              />
            ))}
          </div>
        )}
      </div>
      <div className="p-3 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="font-heading text-[15px] leading-tight truncate">{set.title}</div>
          <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#595959] mt-0.5 truncate">
            @{set.author?.username || "anon"}
          </div>
        </div>
        <div className="flex items-center gap-2 text-[11px] font-mono text-[#595959] shrink-0">
          <span className="flex items-center gap-1"><Heart className="w-3 h-3" /> {set.likes_count || 0}</span>
          <span className="flex items-center gap-1"><MessageCircle className="w-3 h-3" /> {set.comments_count || 0}</span>
        </div>
      </div>
    </Link>
  );
}
